import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { currencyAPI, tenantConfigAPI } from '@matix/api-client';
import { api } from '../../lib/api';
import { setCurrency } from '../../lib/currency';
import { useAuthStore } from '../../lib/authStore';
import { useUiStore } from '../../lib/uiStore';
import { useLang } from '../../lib/lang';
import { CalendarView } from '../calendar/CalendarView';
import { WeekView } from '../calendar/WeekView';
import { MonthView } from '../calendar/MonthView';
import { DashboardView } from '../dashboard/DashboardView';
import { ReportsView } from '../reports/ReportsView';
import { AuditView } from '../audit/AuditView';
import { BookingDialog } from '../booking/BookingDialog';

export function AppShell(): JSX.Element {
  const session = useAuthStore((s) => s.session);
  const logout = useAuthStore((s) => s.logout);
  const { t, lang, toggle } = useLang();
  const { branchId, date, view, page, setBranch, setView, setPage, shiftDate, today, openCreate, booking } = useUiStore();
  /** The phone-only overflow menu (manual / language / account / sign out). */
  const [menuOpen, setMenuOpen] = useState(false);

  const locale = lang === 'ar' ? 'ar' : 'en-US';
  const dateLabel =
    view === 'month'
      ? new Date(`${date}T00:00:00`).toLocaleDateString(locale, { month: 'long', year: 'numeric' })
      : new Date(`${date}T00:00:00`).toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });

  const branchesQ = useQuery({ queryKey: ['branches'], queryFn: api.branches });
  const cfgQ = useQuery({ queryKey: ['tenant-config'], queryFn: () => tenantConfigAPI.get() });

  // The tenant's base currency decides how many decimals every price and
  // revenue figure in this app is formatted at (3 on KWD, 2 on EGP/SAR/AED).
  // Published DURING render, not in an effect: the formatter is a plain
  // function reading a module store, so a late write would leave the first
  // paint showing 2-decimal money with nothing to trigger a correction.
  const currencyQ = useQuery({
    queryKey: ['tenant-currency'],
    queryFn: () => currencyAPI.get(),
    staleTime: Infinity,
    gcTime: Infinity,
  });
  setCurrency(currencyQ.data);

  useEffect(() => {
    if (branchId == null && session) setBranch(session.branch_id);
  }, [branchId, session, setBranch]);

  const pages = [
    ['calendar', { en: 'Calendar', ar: 'التقويم' }],
    ['dashboard', { en: 'Dashboard', ar: 'لوحة التحكم' }],
    ['reports', { en: 'Reports', ar: 'التقارير' }],
    ['audit', { en: 'Audit', ar: 'السجل' }],
  ] as const;
  const views = [
    ['day', { en: 'Day', ar: 'يوم' }],
    ['week', { en: 'Week', ar: 'أسبوع' }],
    ['month', { en: 'Month', ar: 'شهر' }],
  ] as const;

  const langBtn = (
    <button className="appt-btn-ghost !px-2.5 !py-1.5 text-xs font-bold" onClick={toggle} title={lang === 'ar' ? 'English' : 'العربية'}>
      {lang === 'ar' ? 'EN' : 'ع'}
    </button>
  );

  // The bilingual manual is a static page in /public — opened in its own tab so
  // reception can keep it beside the calendar. BASE_URL keeps it correct if the
  // app is ever served from a sub-path.
  // ?lang hands the manual the language the app is in, so no second toggle.
  const manualUrl = `${import.meta.env.BASE_URL.replace(/\/$/, '')}/manual.html?lang=${lang}`;
  const manualBtn = (
    <a
      className="appt-btn-ghost !px-2"
      href={manualUrl}
      target="_blank"
      rel="noopener noreferrer"
      title={t({ en: 'User manual', ar: 'دليل المستخدم' })}
      aria-label={t({ en: 'User manual', ar: 'دليل المستخدم' })}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
        <path d="M9 7h7M9 11h5" />
      </svg>
    </a>
  );

  if (cfgQ.data && !cfgQ.data.appointments_enabled) {
    return (
      <div className="flex h-[100dvh] flex-col items-center justify-center gap-3 bg-slate-100 p-8 text-center dark:bg-slate-950">
        <div className="text-lg font-semibold text-slate-700 dark:text-slate-200">
          {t({ en: 'Appointments module is not enabled', ar: 'وحدة المواعيد غير مفعّلة' })}
        </div>
        <p className="max-w-sm text-sm text-slate-500">
          {t({
            en: 'This workspace doesn’t have the Appointments module enabled. An administrator can enable it in Admin → POS Settings → Modules.',
            ar: 'وحدة المواعيد غير مفعّلة لهذه الشركة. يمكن للمسؤول تفعيلها من الإدارة ← إعدادات نقطة البيع ← الوحدات.',
          })}
        </p>
        <button className="appt-btn-ghost" onClick={logout}>{t({ en: 'Sign out', ar: 'تسجيل الخروج' })}</button>
      </div>
    );
  }

  /*
   * The header carries eleven controls. On a desktop they fit in one row; on a
   * phone they used to wrap into four or five, eating most of the screen before
   * the calendar got any. So the same controls are dealt out differently by
   * width:
   *
   *   < md   row 1: brand · branch · New · overflow menu
   *          row 2: date nav (calendar page only)
   *          bottom: page tabs
   *   ≥ md   one row, exactly as before.
   *
   * Page navigation moves to a bottom tab bar rather than being hidden in the
   * overflow menu: it is the most-used control in the app and a thumb reaches
   * the bottom of a phone far more easily than the top.
   */
  const dateInput = (
    <input
      type="date"
      className="appt-input min-w-0 flex-1 py-1.5 text-xs sm:w-[150px] sm:flex-none sm:text-sm"
      value={date}
      onChange={(e) => e.target.value && useUiStore.getState().setDate(e.target.value)}
      aria-label={t({ en: 'Date', ar: 'التاريخ' })}
    />
  );

  const dateNav = (
    <div className="flex items-center gap-1">
      <button className="appt-btn-ghost !px-2.5" onClick={() => shiftDate(-1)} aria-label={t({ en: 'Previous', ar: 'السابق' })}>‹</button>
      {/* "Today" is a shortcut the native date picker already covers, and on a
          phone the row cannot afford both — so below sm the picker itself does
          the jumping and the arrows do the stepping. */}
      <button className="appt-btn-ghost !py-1.5 hidden text-xs sm:inline-flex" onClick={today}>{t({ en: 'Today', ar: 'اليوم' })}</button>
      <button className="appt-btn-ghost !px-2.5" onClick={() => shiftDate(1)} aria-label={t({ en: 'Next', ar: 'التالي' })}>›</button>
      {dateInput}
      <span className="ms-2 hidden text-sm font-medium text-slate-500 lg:block">{dateLabel}</span>
      <div className="appt-seg ms-1 flex-none sm:ms-2">
        {views.map(([v, label]) => (
          <button key={v} aria-pressed={view === v} onClick={() => setView(v)}>{t(label)}</button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="flex h-[100dvh] flex-col">
      <header className="z-20 flex-none border-b border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="flex min-h-[60px] flex-wrap items-center gap-2 px-3 py-2 sm:min-h-[68px] sm:gap-3 sm:px-5 sm:py-3">
          <div className="flex flex-none items-center gap-2 pe-1 sm:pe-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-brand text-white sm:h-10 sm:w-10">
              <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <rect x="3" y="4" width="18" height="17" rx="2" />
                <path d="M3 9h18M8 3v4M16 3v4" />
              </svg>
            </div>
            <span className="hidden text-sm font-bold text-slate-800 lg:block dark:text-slate-100">{t({ en: 'Appointments', ar: 'المواعيد' })}</span>
          </div>

          {/* Page nav — the bottom tab bar takes over below md */}
          <div className="appt-seg hidden md:inline-flex">
            {pages.map(([p, label]) => (
              <button key={p} aria-pressed={page === p} onClick={() => setPage(p)}>{t(label)}</button>
            ))}
          </div>

          {/* Branch selector. Allowed to shrink on a phone; capped on desktop. */}
          <select
            className="appt-input min-w-0 flex-1 py-1.5 sm:max-w-[190px] sm:flex-none"
            value={branchId ?? ''}
            onChange={(e) => setBranch(Number(e.target.value))}
            aria-label={t({ en: 'Branch', ar: 'الفرع' })}
          >
            {(branchesQ.data ?? []).map((b) => (
              <option key={b.id} value={b.id}>{b.name ?? t({ en: `Branch ${b.id}`, ar: `فرع ${b.id}` })}</option>
            ))}
          </select>

          {/* Date nav sits inline from md; below that it gets its own row. */}
          {page === 'calendar' && <div className="ms-1 hidden md:block">{dateNav}</div>}

          <div className="ms-auto flex flex-none items-center gap-1.5 sm:gap-2">
            <button className="appt-btn-primary !px-3 sm:!px-3.5" onClick={() => openCreate(null)}>
              <span className="text-base leading-none">+</span>
              <span className="hidden sm:inline">{t({ en: 'New', ar: 'جديد' })}</span>
            </button>
            <div className="hidden items-center gap-2 sm:flex">
              {manualBtn}
              {langBtn}
              <div className="hidden text-end lg:block">
                <div className="text-xs font-semibold text-slate-700 dark:text-slate-200">{session?.username}</div>
                <div className="text-[11px] text-slate-400">{session?.subsidiary_name}</div>
              </div>
              <button className="appt-btn-ghost !px-2" onClick={logout} title={t({ en: 'Sign out', ar: 'تسجيل الخروج' })} aria-label={t({ en: 'Sign out', ar: 'تسجيل الخروج' })}>
                <SignOutIcon />
              </button>
            </div>
            {/* Everything that did not fit, on a phone. Controlled rather than
                a <details>, which would stay open after a pick and has no
                dismiss-on-outside-tap. */}
            <div className="relative sm:hidden">
              <button
                className="appt-btn-ghost !px-2.5"
                onClick={() => setMenuOpen((v) => !v)}
                aria-expanded={menuOpen}
                aria-label={t({ en: 'More', ar: 'المزيد' })}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                  <circle cx="5" cy="12" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="19" cy="12" r="2" />
                </svg>
              </button>
              {menuOpen && (
                <>
                  <div className="fixed inset-0 z-20" onClick={() => setMenuOpen(false)} aria-hidden />
                  <div className="absolute end-0 z-30 mt-1 w-52 rounded-xl border border-slate-200 bg-white p-2 shadow-xl dark:border-slate-700 dark:bg-slate-800">
                    <div className="px-2 pb-2 text-xs">
                      <div className="truncate font-semibold text-slate-700 dark:text-slate-200">{session?.username}</div>
                      <div className="truncate text-[11px] text-slate-400">{session?.subsidiary_name}</div>
                    </div>
                    <a
                      className="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-sm text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-700"
                      href={manualUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setMenuOpen(false)}
                    >
                      {t({ en: 'User manual', ar: 'دليل المستخدم' })}
                    </a>
                    <button
                      className="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-sm text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-700"
                      onClick={() => { toggle(); setMenuOpen(false); }}
                    >
                      {lang === 'ar' ? 'English' : 'العربية'}
                    </button>
                    <button
                      className="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40"
                      onClick={logout}
                    >
                      <SignOutIcon />
                      {t({ en: 'Sign out', ar: 'تسجيل الخروج' })}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Second row on a phone: the date controls, which need the full width. */}
        {page === 'calendar' && (
          <div className="border-t border-slate-100 px-3 py-2 md:hidden dark:border-slate-800">{dateNav}</div>
        )}
      </header>

      <main className="min-h-0 flex-1">
        {branchId != null &&
          (page === 'dashboard' ? (
            <DashboardView branchId={branchId} />
          ) : page === 'reports' ? (
            <ReportsView branchId={branchId} />
          ) : page === 'audit' ? (
            <AuditView />
          ) : view === 'month' ? (
            <MonthView branchId={branchId} date={date} />
          ) : view === 'week' ? (
            <WeekView branchId={branchId} date={date} />
          ) : (
            <CalendarView branchId={branchId} date={date} />
          ))}
      </main>

      <nav className="appt-tabbar" aria-label={t({ en: 'Sections', ar: 'الأقسام' })}>
        {pages.map(([p, label]) => (
          <button
            key={p}
            onClick={() => setPage(p)}
            aria-current={page === p ? 'page' : undefined}
            className={page === p ? 'text-brand' : 'text-slate-400'}
          >
            <PageIcon page={p} />
            {t(label)}
          </button>
        ))}
      </nav>

      {booking && <BookingDialog key={booking.sessionId ?? 'new'} />}
    </div>
  );
}

function SignOutIcon(): JSX.Element {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
    </svg>
  );
}

function PageIcon({ page }: { page: 'calendar' | 'dashboard' | 'reports' | 'audit' }): JSX.Element {
  const common = { width: 20, height: 20, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, 'aria-hidden': true };
  if (page === 'calendar') {
    return <svg {...common}><rect x="3" y="4" width="18" height="17" rx="2" /><path d="M3 9h18M8 3v4M16 3v4" /></svg>;
  }
  if (page === 'dashboard') {
    return <svg {...common}><path d="M3 13h6V3H3zM13 21h8V11h-8zM13 7h8V3h-8zM3 21h6v-4H3z" /></svg>;
  }
  if (page === 'reports') {
    return <svg {...common}><path d="M4 20V10M10 20V4M16 20v-7M22 20H2" /></svg>;
  }
  return <svg {...common}><path d="M12 8v4l3 2" /><circle cx="12" cy="12" r="9" /></svg>;
}
